import { KeyboardEvent, useEffect, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import useConversations from "admin/hooks/useConversations";
import type Conversation from "admin/types/interfaces/conversation";
import { formatDistanceToNowStrict } from "date-fns";
import {
  ArrowDown,
  ArrowUp,
  ChevronLeft,
  ChevronRight,
  MessageSquare,
  Plus,
  Search,
} from "lucide-react";
import { Button } from "~/components/ui/button";
import { Input } from "~/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "~/components/ui/select";
import { Skeleton } from "~/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "~/components/ui/table";
import useUser from "~/contexts/user";
import { TabHeader } from "~/pages/manifold-nextgen/components/TabHeader";
import { AgentAvatar } from "~/pages/manifold-nextgen/components/agents/AgentAvatar";
import { AgentAvatarStack } from "~/pages/manifold-nextgen/components/agents/AgentAvatarStack";
import {
  MIN_SEARCH_LENGTH,
  type SessionSearchResult,
  stripEmphasis,
  useSearchSessions,
} from "~/pages/manifold-nextgen/components/home/useSearchSessions";
import {
  nextgenConversationPath,
  nextgenProjectChatsPath,
  nextgenProjectFeaturedSessionPath,
} from "~/pages/manifold-nextgen/paths";
import { Agent } from "~/services/agents";
import { FeaturedSession } from "~/services/projects";
import { ConversationActionsMenu } from "./ConversationActionsMenu";
import { DeleteConversationDialog } from "./DeleteConversationDialog";
import { ShareConversationDialog } from "./ShareConversationDialog";
import {
  useFeaturedSessionAgents,
  useFeaturedSessionServedAgents,
} from "./useFeaturedSessionAgents";
import { useFeaturedSessionsMutations } from "./useFeaturedSessionsMutations";
import { useProjectContext } from "./useProjectContext";
import { useSessionActions } from "./useSessionActions";

const PAGE_SIZE = 20;

interface SessionListRowProps {
  projectId: string;
  session: Conversation;
  agent: Agent | undefined;
  // Resolved from `session.agentIds` against the project's agent roster.
  // Rendered as a stack only when there's more than one; otherwise the
  // existing single-agent path below reads `agent` as before.
  agents: Agent[];
  isRenaming: boolean;
  onStartRename: () => void;
  onCommitRename: (next: string) => void;
  onEndRename: () => void;
  onAutoRename: () => void;
  isAutoRenaming: boolean;
  onShare: () => void;
  onDelete: () => void;
  // Whether the current viewer authored this session — non-owners get no
  // rename/share/delete actions, since it isn't theirs to manage.
  isOwnSession: boolean;
}

const SessionListRow = ({
  projectId,
  session,
  agent,
  agents,
  isRenaming,
  onStartRename,
  onCommitRename,
  onEndRename,
  onAutoRename,
  isAutoRenaming,
  onShare,
  onDelete,
  isOwnSession,
}: SessionListRowProps) => {
  const navigate = useNavigate();
  const [draft, setDraft] = useState(session.displayName);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (isRenaming) {
      setDraft(session.displayName);
      // A plain rAF loses this race to Radix's own focus-return-to-trigger
      // behavior when the dropdown menu closes. setTimeout (a macrotask)
      // reliably runs after that, so our focus call wins instead.
      const id = window.setTimeout(() => {
        inputRef.current?.focus();
        inputRef.current?.select();
      }, 0);
      return () => window.clearTimeout(id);
    }
  }, [isRenaming, session.displayName]);

  const commit = () => onCommitRename(draft);

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      e.preventDefault();
      commit();
    } else if (e.key === "Escape") {
      e.preventDefault();
      onEndRename();
    }
  };

  return (
    <TableRow
      className="group cursor-pointer"
      onClick={() => {
        if (!isRenaming) {
          navigate(nextgenConversationPath(projectId, session.id));
        }
      }}
    >
      <TableCell>
        <div className="relative min-h-5 min-w-0">
          {isRenaming ? (
            <input
              ref={inputRef}
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              onKeyDown={handleKeyDown}
              onBlur={commit}
              onClick={(e) => e.stopPropagation()}
              aria-label="Rename session"
              className="absolute inset-0 rounded border border-border bg-background px-1 text-sm font-medium text-foreground outline-none focus:border-primary"
            />
          ) : (
            <span className="truncate text-sm font-medium">{session.displayName}</span>
          )}
        </div>
      </TableCell>
      <TableCell>
        <div className="flex min-w-0 items-center gap-2">
          {agents.length > 1 ? (
            <AgentAvatarStack agents={agents} size={20} />
          ) : (
            <>
              {agent ? (
                <AgentAvatar agent={agent} size={20} />
              ) : (
                <div
                  className="flex size-5 shrink-0 items-center justify-center text-muted-foreground"
                  aria-hidden="true"
                >
                  <MessageSquare className="size-3.5" />
                </div>
              )}
              <span className="truncate text-[13px] text-muted-foreground">
                {agent?.display_name ?? "—"}
              </span>
            </>
          )}
        </div>
      </TableCell>
      <TableCell className="text-right">
        <span className="truncate text-[13px] text-muted-foreground">
          {formatDistanceToNowStrict(new Date(session.updatedAt), { addSuffix: true })}
        </span>
      </TableCell>
      <TableCell onClick={(e) => e.stopPropagation()}>
        <div className="flex min-h-6 items-center justify-end">
          {!isRenaming && isOwnSession && (
            <ConversationActionsMenu
              onShare={onShare}
              onRename={onStartRename}
              onAutoRename={onAutoRename}
              isAutoRenaming={isAutoRenaming}
              onDelete={onDelete}
            />
          )}
        </div>
      </TableCell>
    </TableRow>
  );
};

// A content-search hit. Unlike a browse row it is read-only — click to open,
// with the matched snippet as a second line for context. Managing a session
// (rename/share/delete) stays on the unfiltered list, so a search result never
// goes stale against a mutation it can't see.
const SearchResultRow = ({
  projectId,
  result,
  agent,
}: {
  projectId: string;
  result: SessionSearchResult;
  agent: Agent | undefined;
}) => {
  const navigate = useNavigate();

  return (
    <TableRow
      className="group cursor-pointer"
      onClick={() => navigate(nextgenConversationPath(projectId, result.id))}
    >
      <TableCell>
        <div className="min-w-0">
          <span className="block truncate text-sm font-medium">{result.displayName}</span>
          {result.snippet && (
            <span className="mt-0.5 block truncate text-[13px] text-muted-foreground">
              {stripEmphasis(result.snippet)}
            </span>
          )}
        </div>
      </TableCell>
      <TableCell>
        <div className="flex min-w-0 items-center gap-2">
          {agent ? (
            <AgentAvatar agent={agent} size={20} />
          ) : (
            <div
              className="flex size-5 shrink-0 items-center justify-center text-muted-foreground"
              aria-hidden="true"
            >
              <MessageSquare className="size-3.5" />
            </div>
          )}
          <span className="truncate text-[13px] text-muted-foreground">
            {agent?.display_name ?? "—"}
          </span>
        </div>
      </TableCell>
      <TableCell className="text-right">
        <span className="truncate text-[13px] text-muted-foreground">
          {formatDistanceToNowStrict(new Date(result.updatedAt), { addSuffix: true })}
        </span>
      </TableCell>
      <TableCell />
    </TableRow>
  );
};

interface FeaturedSessionListRowProps {
  projectId: string;
  featuredSession: FeaturedSession;
  agent: Agent | undefined;
  isOwner: boolean;
  onUnfeature: () => void;
}

// A rail row links to the read-only view scoped to this project rather than
// the standalone share page — the rail carries only a share token, never a
// conversation id, so this renders from that token. It can only be
// unfeatured, never renamed or reshared.
const FeaturedSessionListRow = ({
  projectId,
  featuredSession,
  agent,
  isOwner,
  onUnfeature,
}: FeaturedSessionListRowProps) => {
  const navigate = useNavigate();
  const agents = useFeaturedSessionServedAgents(featuredSession.share_token, agent);

  return (
    <TableRow
      className="group cursor-pointer"
      onClick={() =>
        navigate(nextgenProjectFeaturedSessionPath(projectId, featuredSession.share_token))
      }
    >
      <TableCell>
        <span className="truncate text-sm font-medium">{featuredSession.display_name}</span>
      </TableCell>
      <TableCell>
        <div className="flex min-w-0 items-center gap-2">
          {agents.length > 1 ? (
            <AgentAvatarStack agents={agents} size={20} />
          ) : (
            <>
              {agents[0] ? (
                <AgentAvatar agent={agents[0]} size={20} />
              ) : (
                <div
                  className="flex size-5 shrink-0 items-center justify-center text-muted-foreground"
                  aria-hidden="true"
                >
                  <MessageSquare className="size-3.5" />
                </div>
              )}
              <span className="truncate text-[13px] text-muted-foreground">
                {agents[0]?.display_name ?? "—"}
              </span>
            </>
          )}
        </div>
      </TableCell>
      <TableCell className="text-right">
        <span className="truncate text-[13px] text-muted-foreground">
          {formatDistanceToNowStrict(new Date(featuredSession.created_at), { addSuffix: true })}
        </span>
      </TableCell>
      <TableCell onClick={(e) => e.stopPropagation()}>
        <div className="flex min-h-6 items-center justify-end">
          {isOwner && <ConversationActionsMenu onUnfeature={onUnfeature} />}
        </div>
      </TableCell>
    </TableRow>
  );
};

// Mirrors a real row's column widths so the table doesn't jump in size once
// data replaces it, and keeps the header/border shell in place instead of
// swapping to a bare list of skeleton rows.
const SessionsTableSkeletonRow = () => (
  <TableRow>
    <TableCell>
      <Skeleton className="h-4 w-48" />
    </TableCell>
    <TableCell>
      <div className="flex items-center gap-2">
        <Skeleton className="size-5 shrink-0 rounded-full" />
        <Skeleton className="h-4 w-24" />
      </div>
    </TableCell>
    <TableCell className="text-right">
      <Skeleton className="ml-auto h-4 w-16" />
    </TableCell>
    <TableCell />
  </TableRow>
);

interface SessionsTableSkeletonProps {
  thirdColumnLabel: string;
  // Matches the caller's own row cap, so the skeleton's height doesn't shift
  // once real rows replace it.
  rowCount: number;
}

const SessionsTableSkeleton = ({ thirdColumnLabel, rowCount }: SessionsTableSkeletonProps) => (
  <div className="rounded-lg border border-border" role="status" aria-busy="true">
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>Session</TableHead>
          <TableHead className="w-56">Agent</TableHead>
          <TableHead className="w-40 text-right">{thirdColumnLabel}</TableHead>
          <TableHead className="w-10">
            <span className="sr-only">Actions</span>
          </TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {Array.from({ length: rowCount }).map((_, i) => (
          <SessionsTableSkeletonRow key={i} />
        ))}
      </TableBody>
    </Table>
  </div>
);

const FeaturedSessionsTable = ({
  projectId,
  featuredSessions,
  attachedAgentsWithDetails,
  isOwner,
}: {
  projectId: string;
  featuredSessions: FeaturedSession[];
  attachedAgentsWithDetails: Agent[] | undefined;
  isOwner: boolean;
}) => {
  const agentsById = useFeaturedSessionAgents(featuredSessions, attachedAgentsWithDetails);
  const { unfeature } = useFeaturedSessionsMutations(projectId);

  if (featuredSessions.length === 0) {
    return <p className="px-4 py-3 text-sm italic text-muted-foreground">No featured sessions.</p>;
  }

  return (
    <div className="rounded-lg border border-border">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Session</TableHead>
            <TableHead className="w-56">Agent</TableHead>
            <TableHead className="w-40 text-right">Curated</TableHead>
            <TableHead className="w-10">
              <span className="sr-only">Actions</span>
            </TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {featuredSessions.map((featuredSession) => (
            <FeaturedSessionListRow
              key={featuredSession.id}
              projectId={projectId}
              featuredSession={featuredSession}
              agent={
                featuredSession.agent_id ? agentsById.get(featuredSession.agent_id) : undefined
              }
              isOwner={isOwner}
              onUnfeature={() => void unfeature(featuredSession)}
            />
          ))}
        </TableBody>
      </Table>
    </div>
  );
};

const SessionSearchResults = ({
  projectId,
  results,
  agentsById,
  isLoading,
  hasMore,
  onLoadMore,
}: {
  projectId: string;
  results: SessionSearchResult[];
  agentsById: Map<string, Agent>;
  isLoading: boolean;
  hasMore: boolean;
  onLoadMore: () => void;
}) => {
  if (isLoading && results.length === 0) {
    return <SessionsTableSkeleton thirdColumnLabel="Updated" rowCount={5} />;
  }
  if (results.length === 0) {
    return <p className="px-4 py-3 text-sm italic text-muted-foreground">No matching sessions.</p>;
  }

  return (
    <>
      <div className="rounded-lg border border-border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Session</TableHead>
              <TableHead className="w-56">Agent</TableHead>
              <TableHead className="w-40 text-right">Updated</TableHead>
              <TableHead className="w-10">
                <span className="sr-only">Actions</span>
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {results.map((result) => (
              <SearchResultRow
                key={result.id}
                projectId={projectId}
                result={result}
                agent={result.agentId ? agentsById.get(result.agentId) : undefined}
              />
            ))}
          </TableBody>
        </Table>
      </div>
      {hasMore && (
        <Button variant="outline" size="sm" className="self-start" onClick={onLoadMore}>
          Load more
        </Button>
      )}
    </>
  );
};

interface FeaturedViewState {
  view: "all" | "featured";
}

const isFeaturedViewState = (state: unknown): state is FeaturedViewState =>
  typeof state === "object" &&
  state !== null &&
  "view" in state &&
  (state.view === "all" || state.view === "featured");

export const ProjectSessionsList = () => {
  const {
    projectId,
    project,
    attachedAgents,
    attachedAgentsWithDetails,
    isOwner,
    featuredSessions,
  } = useProjectContext();
  const currentUser = useUser();
  // The sidebar's "View all" link under Featured Sessions opens straight
  // into that tab via router state, rather than always landing on "Your
  // Sessions" and making the user click through again.
  const location = useLocation();
  const initialView = isFeaturedViewState(location.state) ? location.state.view : undefined;
  const [page, setPage] = useState(1);
  const [agentFilter, setAgentFilter] = useState("all");
  const [view, setView] = useState<"all" | "featured">(
    initialView === "featured" ? "featured" : "all"
  );
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("desc");
  const [query, setQuery] = useState("");

  const agentIdFilter = agentFilter === "all" ? undefined : agentFilter;

  // A project session's `resource_id` is the project's UUID, so that is what the
  // conversations service has to be queried on. `projectId` comes from the URL
  // and may be a short_id, so it is only good for building links.
  const resourceId = project.id;

  const {
    data: sessions,
    total,
    isLoading,
    mutate: search,
  } = useConversations({
    resourceId,
    agentId: agentIdFilter,
    limit: PAGE_SIZE,
    offset: (page - 1) * PAGE_SIZE,
    sortKey: "updated_at",
    sortOrder,
    nextgenOnly: true,
    isAdmin: false,
    enabled: view === "all",
  });

  const refetch = () => search({ resource_id: resourceId, agent_id: agentIdFilter });

  // Content search is scoped to this project and swaps in for the browse list
  // once the query is long enough to be meaningful. It never runs on the
  // Featured tab.
  const isSearching = view === "all" && query.trim().length >= MIN_SEARCH_LENGTH;
  const {
    results: searchResults,
    hasMore: searchHasMore,
    isLoading: isSearchLoading,
    loadMore: loadMoreSearch,
  } = useSearchSessions(query, { resourceId, limit: PAGE_SIZE });

  const {
    renamingId,
    startRename,
    commitRename,
    endRename,
    autoRenamingId,
    autoRename,
    actionTarget,
    setActionTarget,
    confirmDelete,
  } = useSessionActions(projectId, { onMutated: refetch });

  const agentsById = new Map((attachedAgents ?? []).map((a) => [a.id, a]));
  const pageCount = total ? Math.ceil(total / PAGE_SIZE) : 1;

  return (
    <div className="flex-1 overflow-y-auto p-6">
      <div className="mx-auto flex max-w-3xl flex-col gap-4">
        <TabHeader
          title={view === "featured" ? "Featured Sessions" : "Your Sessions"}
          context={
            view === "featured"
              ? featuredSessions !== undefined
                ? `${featuredSessions.length} session${featuredSessions.length === 1 ? "" : "s"}`
                : undefined
              : total !== undefined
                ? `${total} session${total === 1 ? "" : "s"}`
                : undefined
          }
          action={
            <Button
              asChild
              variant="ghost"
              size="sm"
              className="text-primary hover:text-primary/80"
            >
              <Link to={nextgenProjectChatsPath(projectId)}>
                <Plus className="size-4" />
                New Session
              </Link>
            </Button>
          }
        />

        <div className="flex items-center gap-2">
          {/* Show the input when the project has sessions to search, and keep it
              mounted while a search is active even if an agent filter drops the
              browse total to 0 — otherwise the empty state strands the query
              with no visible way to clear it. */}
          {view === "all" && ((total ?? 0) > 0 || isSearching) && (
            <div className="relative w-64">
              <Search
                className="pointer-events-none absolute left-2 top-1/2 size-4 -translate-y-1/2 text-muted-foreground"
                aria-hidden="true"
              />
              <Input
                type="search"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Escape") {
                    setQuery("");
                  }
                }}
                placeholder="Search sessions..."
                aria-label="Search sessions in this project"
                className="h-9 pl-8"
              />
            </div>
          )}
          <div className="ml-auto flex items-center gap-2">
            <Select
              value={view}
              onValueChange={(value) => {
                setView(value as "all" | "featured");
                setPage(1);
                setQuery("");
              }}
            >
              <SelectTrigger aria-label="Filter by featured" className="w-[160px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Your Sessions</SelectItem>
                <SelectItem value="featured">Featured</SelectItem>
              </SelectContent>
            </Select>
            {view === "all" && (
              <Select
                value={agentFilter}
                onValueChange={(value) => {
                  setAgentFilter(value);
                  setPage(1);
                }}
              >
                <SelectTrigger aria-label="Filter by agent" className="w-[180px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Agents</SelectItem>
                  {(attachedAgents ?? []).map((agent) => (
                    <SelectItem key={agent.id} value={agent.id}>
                      {agent.display_name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          </div>
        </div>

        {view === "featured" ? (
          featuredSessions === undefined ? (
            <SessionsTableSkeleton thirdColumnLabel="Curated" rowCount={5} />
          ) : (
            <FeaturedSessionsTable
              projectId={projectId}
              featuredSessions={featuredSessions}
              attachedAgentsWithDetails={attachedAgentsWithDetails}
              isOwner={isOwner}
            />
          )
        ) : isSearching ? (
          <SessionSearchResults
            projectId={projectId}
            results={searchResults}
            agentsById={agentsById}
            isLoading={isSearchLoading}
            hasMore={searchHasMore}
            onLoadMore={loadMoreSearch}
          />
        ) : isLoading ? (
          <SessionsTableSkeleton thirdColumnLabel="Updated" rowCount={PAGE_SIZE} />
        ) : sessions.length === 0 ? (
          <p className="px-4 py-3 text-sm italic text-muted-foreground">No sessions yet.</p>
        ) : (
          <>
            <div className="rounded-lg border border-border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Session</TableHead>
                    <TableHead className="w-56">Agent</TableHead>
                    <TableHead className="w-40 text-right">
                      <button
                        type="button"
                        onClick={() => {
                          setSortOrder((prev) => (prev === "desc" ? "asc" : "desc"));
                          setPage(1);
                        }}
                        className="inline-flex items-center gap-1 cursor-pointer transition-colors hover:text-foreground"
                      >
                        Updated
                        {sortOrder === "asc" ? (
                          <ArrowUp className="size-3.5" aria-hidden="true" />
                        ) : (
                          <ArrowDown className="size-3.5" aria-hidden="true" />
                        )}
                      </button>
                    </TableHead>
                    <TableHead className="w-10">
                      <span className="sr-only">Actions</span>
                    </TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {sessions.map((session) => (
                    <SessionListRow
                      key={session.id}
                      projectId={projectId}
                      session={session}
                      agent={session.agentId ? agentsById.get(session.agentId) : undefined}
                      agents={(session.agentIds ?? [])
                        .map((id) => agentsById.get(id))
                        .filter((a): a is Agent => a != null)}
                      isRenaming={renamingId === session.id}
                      onStartRename={() => startRename(session.id)}
                      onCommitRename={(next) =>
                        commitRename({ id: session.id, display_name: session.displayName }, next)
                      }
                      onEndRename={endRename}
                      onAutoRename={() =>
                        void autoRename({ id: session.id, display_name: session.displayName })
                      }
                      isAutoRenaming={autoRenamingId === session.id}
                      onShare={() =>
                        setActionTarget({
                          session: {
                            id: session.id,
                            display_name: session.displayName,
                            agent_id: session.agentId,
                          },
                          action: "share",
                        })
                      }
                      onDelete={() =>
                        setActionTarget({
                          session: { id: session.id, display_name: session.displayName },
                          action: "delete",
                        })
                      }
                      isOwnSession={session.userId === currentUser?.email}
                    />
                  ))}
                </TableBody>
              </Table>
            </div>

            {pageCount > 1 && (
              <div className="flex items-center justify-between">
                <span className="text-sm text-muted-foreground">
                  Page {page} of {pageCount}
                </span>
                <div className="flex items-center gap-1">
                  <Button
                    variant="outline"
                    size="icon"
                    aria-label="Previous page"
                    onClick={() => setPage((p) => p - 1)}
                    disabled={page === 1}
                  >
                    <ChevronLeft />
                  </Button>
                  <Button
                    variant="outline"
                    size="icon"
                    aria-label="Next page"
                    onClick={() => setPage((p) => p + 1)}
                    disabled={page === pageCount}
                  >
                    <ChevronRight />
                  </Button>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      <ShareConversationDialog
        open={actionTarget?.action === "share"}
        onOpenChange={(open) => {
          if (!open) {
            setActionTarget(null);
          }
        }}
        conversationId={actionTarget?.session.id ?? ""}
        projectId={projectId}
        isOwner={isOwner}
        displayName={actionTarget?.session.display_name ?? ""}
        agentId={actionTarget?.session.agent_id}
        featuredSessions={featuredSessions}
      />

      <DeleteConversationDialog
        open={actionTarget?.action === "delete"}
        onOpenChange={(open) => {
          if (!open) {
            setActionTarget(null);
          }
        }}
        conversationTitle={actionTarget?.session.display_name ?? ""}
        onConfirm={confirmDelete}
      />
    </div>
  );
};
